import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import type { Session, User } from '@supabase/supabase-js'
import type { Profile, UserRole } from '@/types/database'

interface AuthState {
  session: Session | null
  user: User | null
  profile: Profile | null
  role: UserRole | null
  isLoading: boolean
  isAuthenticated: boolean
}

interface AuthContextValue extends AuthState {
  signInWithEmail: (email: string) => Promise<{ error: Error | null }>
  signInWithPassword: (email: string, password: string) => Promise<{ error: Error | null }>
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<{ error: Error | null }>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    session: null,
    user: null,
    profile: null,
    role: null,
    isLoading: true,
    isAuthenticated: false,
  })

  const fetchProfile = async (userId: string): Promise<Profile | null> => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle()

    if (error) {
      console.error('Failed to fetch profile:', error.message)
      return null
    }

    // If no profile exists, create one (upsert to handle conflicts)
    if (!data) {
      const { data: newProfile } = await (supabase
        .from('user_profiles') as any)
        .upsert({ id: userId, role: 'farmer' }, { onConflict: 'id' })
        .select()
        .single()
      return newProfile as Profile | null
    }

    // `user_profiles` não está entre as tabelas tipadas explicitamente em
    // src/types/database.ts (fora do escopo da entrega que tipou
    // forum_topics/forum_replies/bioinputs_* -- ver comentário no topo do
    // `Database` naquele arquivo) -- cai no fallback genérico (`any`), então
    // `.select('*')` não resolve para um shape com propriedades conhecidas
    // e precisa do cast duplo aqui, como antes dessa tipagem existir.
    return data as unknown as Profile
  }

  const handleSession = async (session: Session | null) => {
    if (!session?.user) {
      setState({
        session: null,
        user: null,
        profile: null,
        role: null,
        isLoading: false,
        isAuthenticated: false,
      })
      return
    }

    const profile = await fetchProfile(session.user.id)

    setState({
      session,
      user: session.user,
      profile,
      role: (profile?.role as UserRole) ?? null,
      isLoading: false,
      isAuthenticated: true,
    })
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleSession(session)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      handleSession(session)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signInWithEmail = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    })
    return { error: error ? new Error(error.message) : null }
  }

  const signInWithPassword = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error: error ? new Error(error.message) : null }
  }

  const signUp = async (email: string, password: string, fullName: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: { full_name: fullName },
      },
    })

    if (error) return { error: new Error(error.message) }

    // Create profile row
    if (data.user) {
      await supabase.from('user_profiles').insert({
        id: data.user.id,
        full_name: fullName,
        role: 'farmer',
      } as any)
    }

    return { error: null }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login`,
    })
    return { error: error ? new Error(error.message) : null }
  }

  const refreshProfile = async () => {
    if (state.user) {
      const profile = await fetchProfile(state.user.id)
      setState((prev) => ({
        ...prev,
        profile,
        role: (profile?.role as UserRole) ?? null,
      }))
    }
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signInWithEmail,
        signInWithPassword,
        signUp,
        signOut,
        resetPassword,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
